import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import authService from '../../services/authService';
import useAuth from '../../hooks/useAuth';
import './Login.css';

const EyeIcon = ({ off }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
    <circle cx="12" cy="12" r="3" />
    {off && <path d="M3 3l18 18" />}
  </svg>
);

/* Linhas de campo: ocupam o painel inteiro, bem discretas */
const PitchLines = () => (
  <svg className="pitch-lines" viewBox="0 0 600 800" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeWidth="3">
      <rect x="40" y="40" width="520" height="720" />
      <line x1="40" y1="400" x2="560" y2="400" />
      <circle cx="300" cy="400" r="80" />
      <rect x="150" y="40" width="300" height="130" />
      <rect x="220" y="40" width="160" height="50" />
      <rect x="150" y="630" width="300" height="130" />
      <rect x="220" y="710" width="160" height="50" />
    </g>
    <circle cx="300" cy="400" r="5" fill="currentColor" />
  </svg>
);

/* Chaveamento: semifinais -> final -> taça */
const Bracket = () => (
  <svg className="bracket" viewBox="0 0 360 180" role="img"
       aria-label="Chaveamento de mata-mata com semifinais e final">
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="10" width="86" height="28" rx="4" />
      <rect x="2" y="50" width="86" height="28" rx="4" />
      <rect x="2" y="102" width="86" height="28" rx="4" />
      <rect x="2" y="142" width="86" height="28" rx="4" />
      <path d="M88 24h22v20h-22M88 64h22V44M110 44h22" />
      <path d="M88 116h22v20h-22M88 156h22V136M110 136h22" />
      <rect x="132" y="30" width="86" height="28" rx="4" />
      <rect x="132" y="122" width="86" height="28" rx="4" />
      <path d="M218 44h24v46M218 136h24V90M242 90h22" />
    </g>
    <g className="bracket-final">
      <rect x="264" y="74" width="94" height="32" rx="4" fill="none" stroke="currentColor" strokeWidth="2" />
      <text x="311" y="95" textAnchor="middle" fontSize="14" fontWeight="700">FINAL</text>
    </g>
  </svg>
);

const Login = () => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const navigate = useNavigate();
  const { login, user, loadingAuth } = useAuth();
  const location = useLocation();

  const performRedirect = useCallback(() => {
    if (user) {
      const queryParams = new URLSearchParams(location.search);
      const redirectPath = queryParams.get('redirect') || location.state?.from?.pathname;
      if (redirectPath) {
        navigate(decodeURIComponent(redirectPath), { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, location, navigate]);

  useEffect(() => {
    if (!loadingAuth && user) performRedirect();
  }, [user, loadingAuth, performRedirect]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!credentials.email.includes('@')) {
      setError('Por favor, insira um email válido');
      return;
    }

    if (!credentials.password) {
      setError('Por favor, insira sua senha');
      return;
    }

    setIsLoading(true);
    try {
      const response = await authService.login(credentials);
      localStorage.setItem('token', response.token);
      login(response.user);
    } catch (err) {
      setError(err.message || 'Erro ao conectar com o servidor');
    } finally {
      setIsLoading(false);
    }
  };

  const registerLink = location.search
    ? `/register?redirectAfterLogin=${new URLSearchParams(location.search).get('redirect')}`
    : '/register';

  if (loadingAuth) {
    return (
      <div className="loading-fullscreen">
        <div className="spinner"></div>
        <p>Verificando sessão...</p>
      </div>
    );
  }

  return (
    <main className="login-page">
      <section className="login-hero" aria-hidden="false">
        <PitchLines />
        <div className="hero-content">
          <h2 className="hero-title">
            Do sorteio<br />à taça erguida.
          </h2>
          <p className="hero-text">
            Monte a copa, defina as chaves, lance os resultados e acompanhe a classificação em um só lugar.
          </p>
          <Bracket />
        </div>
      </section>

      <section className="login-panel">
        <div className="login-card">
          <div className="login-brand">
            <span className="login-ball" aria-hidden="true">⚽</span>
            <span className="login-brand-name">Gestão de Campeonatos</span>
          </div>

          <h1>Entrar no campeonato</h1>
          <p className="login-subtitle">Acesse sua conta para gerenciar suas copas.</p>

          <div aria-live="polite">
            {error && <div className="alert error" role="alert">{error}</div>}
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="login-email">Email</label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                value={credentials.email}
                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                placeholder="seu@email.com"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="login-password">Senha</label>
              <div className="password-wrap">
                <input
                  id="login-password"
                  type={passwordVisible ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  placeholder="Sua senha"
                  required
                  className="password-input"
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                  aria-label={passwordVisible ? 'Ocultar senha' : 'Mostrar senha'}
                  aria-pressed={passwordVisible}
                >
                  <EyeIcon off={passwordVisible} />
                </button>
              </div>
            </div>

            <div className="forgot-row">
              <Link to="/recuperar-senha">Esqueceu a senha?</Link>
            </div>

            <button type="submit" className="primary-button" disabled={isLoading}>
              {isLoading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>

          <p className="auth-links">
            Ainda não tem conta? <Link to={registerLink}>Criar conta</Link>
          </p>
        </div>
      </section>
    </main>
  );
};

export default Login;